import { Link } from 'react-router-dom';
import { CategoryGrid } from '@components/category/CategoryGrid';
import { ProductGrid } from '@components/product/ProductGrid';
import { useProducts } from '../hooks/useProducts';

export function HomePage() {
  const { products: featured, isLoading } = useProducts({ featured: true });

  return (
    <div>
      {/* Hero Section — breaks out of the max-w container */}
      <section className="relative -mx-4 -mt-8 overflow-hidden px-4 sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
        <div className="hero-mesh hero-grid relative py-24 sm:py-32 lg:py-40">
          {/* Floating orb accents */}
          <div className="pointer-events-none absolute left-[-5%] top-[-10%] h-[500px] w-[500px] rounded-full bg-green-500/10 blur-[80px] animate-glow-pulse" />
          <div className="pointer-events-none absolute right-[-5%] bottom-[20%] h-[400px] w-[400px] rounded-full bg-teal-500/10 blur-[80px] animate-glow-pulse" style={{ animationDelay: '2s' }} />
          <div className="pointer-events-none absolute left-[30%] top-[40%] h-[300px] w-[300px] rounded-full bg-green-400/5 blur-[80px] animate-glow-pulse" style={{ animationDelay: '4s' }} />

          <div className="relative z-10 mx-auto max-w-4xl text-center">
            <div className="mb-8 flex justify-center">
              <img
                src="/images/edg3_labs_logo_light.svg"
                alt="Edg3 Labs"
                className="h-16 w-auto sm:h-20 lg:h-24 animate-float"
              />
            </div>

            <h1 className="font-display text-5xl font-extrabold tracking-tighter leading-[1.1] text-white sm:text-6xl lg:text-7xl xl:text-8xl">
              <span className="text-gradient-green">Creative</span> Tools for the AI Generation
            </h1>

            <p className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-gray-400 sm:text-xl lg:text-2xl">
              Architecting the future of creativity with cutting-edge tools for modern creators.
            </p>

            <div className="mt-12 flex flex-col items-center gap-6 sm:flex-row sm:justify-center">
              <button
                onClick={() => document.getElementById('categories')?.scrollIntoView({ behavior: 'smooth' })}
                className="font-display inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-green-500 to-teal-500 px-8 py-4 text-lg font-bold text-navy-950 shadow-xl transition-all duration-300 hover:shadow-[0_0_30px_rgba(8,139,126,0.4)] active:scale-95 cursor-pointer"
              >
                Explore Products
              </button>
              <Link
                to="/demos"
                className="font-display inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-8 py-4 text-lg font-bold text-white backdrop-blur-md transition-all duration-300 hover:bg-white/10 active:scale-95"
              >
                <svg className="h-5 w-5 text-teal-400" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 14.5v-9l6 4.5-6 4.5z" />
                </svg>
                Free Demos
              </Link>
            </div>
          </div>

          {/* Bottom fade into content */}
          <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-navy-950 to-transparent" />
        </div>
      </section>

      {/* Trust Bar */}
      <section className="relative -mx-4 border-y border-white/5 bg-navy-950/50 py-12 backdrop-blur-sm sm:-mx-6 lg:-mx-8">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-center gap-8 px-8 opacity-70 md:justify-between md:gap-4">
          <div className="flex items-center gap-3 transition-all hover:opacity-100">
            <svg className="h-7 w-7 text-green-500" fill="currentColor" viewBox="0 0 24 24"><path d="M3 3v8h8V3H3zm6 6H5V5h4v4zm-6 4v8h8v-8H3zm6 6H5v-4h4v4zm4-16v8h8V3h-8zm6 6h-4V5h4v4zm-6 4v8h8v-8h-8zm6 6h-4v-4h4v4z" /></svg>
            <span className="text-lg font-medium tracking-tight font-display">50+ Tools</span>
          </div>
          <div className="hidden h-8 w-px bg-white/10 md:block" />
          <div className="flex items-center gap-3 transition-all hover:opacity-100">
            <svg className="h-7 w-7 text-green-500" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 14.5v-9l6 4.5-6 4.5z" /></svg>
            <span className="text-lg font-medium tracking-tight font-display">Free Demos</span>
          </div>
          <div className="hidden h-8 w-px bg-white/10 md:block" />
          <div className="flex items-center gap-3 transition-all hover:opacity-100">
            <svg className="h-7 w-7 text-green-500" fill="currentColor" viewBox="0 0 24 24"><path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z" /></svg>
            <span className="text-lg font-medium tracking-tight font-display">Instant Download</span>
          </div>
          <div className="hidden h-8 w-px bg-white/10 md:block" />
          <div className="flex items-center gap-3 transition-all hover:opacity-100">
            <svg className="h-7 w-7 text-green-500" fill="currentColor" viewBox="0 0 24 24"><path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-2 16l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z" /></svg>
            <span className="text-lg font-medium tracking-tight font-display">Secure & Reliable</span>
          </div>
        </div>
      </section>

      <div className="space-y-16 pt-8">
        <section id="categories">
          <div className="mb-8 flex items-center gap-4">
            <h2 className="font-display text-2xl font-bold text-white">Browse Categories</h2>
            <div className="h-px flex-1 bg-gradient-to-r from-white/10 to-transparent" />
          </div>
          <CategoryGrid />
        </section>

        <section>
          <div className="mb-8 flex items-center gap-4">
            <h2 className="font-display text-2xl font-bold text-white">Featured Products</h2>
            <div className="h-px flex-1 bg-gradient-to-r from-white/10 to-transparent" />
          </div>
          <ProductGrid products={featured} isLoading={isLoading} />
        </section>
      </div>
    </div>
  );
}
